import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  anchorIsGrey, decodeHeat, focusViewBox, hasPoint, heatRect, heatToDataUrl, locationZones, mapBounds, palletZones,
  polyBox, shopPt, snapFt, svgPoints, viewBoxOf, zoneFocusBox, zoneMapLine,
} from '../utils/shopMap';

// The shop from above (jobq-api GET /shop_map), drawn in FEET: the SVG's viewBox is the shop's own
// frame with y flipped (utils/shopMap svgPt), so walls, racks and zones keep their real sizes at any
// zoom, and marks that must stay readable (anchors, gateways, labels) are sized in screen pixels
// through `ftPerPx`. Width is 100% of the parent; the height follows the view.
//
// Presentational only: no reads, no saves. The page or BeaconLocation hands it the map, the location
// and what to draw on top; dragging a handle or clicking the map calls back with the point in feet
// (snapped to 0.5 ft).
//
// Drawn like a floor plan (Mike, 2026-10-22: "basic as heck"): a concrete floor with a 5 ft / 20 ft
// grid, solid walls with real openings (an overhead door dashed across, a man door with its swing when
// the geometry says which way it opens), racks as racks (blue uprights at each frame, orange beams on
// both faces, bay names once zoomed in, from the item's `bays`), machines as rounded blocks, a north
// arrow (north is +x) and a scale bar.
//
// Pallet zones (kind 'pallet', Mike 2026-10-21): on a map that has them the zones name the floor. With
// a location ONLY its zones are coloured (Mike, 2026-10-22: not every area): the best one filled and
// outlined with a glow and a pulse, named on a solid pill with its strength; the second (shown while
// the best is under 70 %, or within 3 dB) hatched and dashed on an outlined pill. The other zones keep a
// faint dashed edge and a muted name. Without a location (the /shop-map editor) every zone keeps a
// light tint, so they can be told apart while drawing. No heat map and no dot for a zone answer (a dot
// looked exact when it was not); a dock, or a location with no zone answer, still draws its dot.
//
// Layers, bottom to top: 1 floor (areas, grid, pallet zone fills, spot outlines, rooms), 2 coverage
// clipped to the floor (the editor's), 3 walls and doors, 4 racks and machines with labels, 5 columns,
// 6 anchors, 7 gateways, 8 the location (zone outlines + names, or a dot), 9 markers, 10 edit handles
// (+ a zone being drawn), 11 the north arrow and the scale bar.
//
// Colours are CSS variables on .jq-shopmap (light) with a [data-bs-theme="dark"] set, so both themes read.

const ZONE_TINTS = ['13,110,253', '253,126,20', '111,66,193', '32,201,151', '214,51,132', '25,135,84'];
const HIT = { loc: '220,53,69', rough: '253,126,20' };

const C = {
  paper: 'var(--sm-paper)',
  floor: 'var(--sm-floor)',
  grid: 'var(--sm-grid)',
  gridMajor: 'var(--sm-grid-major)',
  ink: 'var(--sm-ink)',
  muted: 'var(--sm-muted)',
  wall: 'var(--sm-wall)',
  rfWall: '#c2410c',
  door: 'var(--sm-door)',
  rackFill: 'var(--sm-rack-fill)',
  beam: 'var(--sm-beam)',
  upright: 'var(--sm-upright)',
  machine: 'var(--sm-machine)',
  machineLine: 'var(--sm-machine-line)',
  room: 'var(--sm-room)',
  zoneLine: 'var(--sm-zone-line)',
  pillText: 'var(--sm-pill-text)',
  pillBg: 'var(--sm-pill-bg)',
  anchor: '#6f42c1',
  anchorGrey: '#9aa0a6',
  gateway: '#198754',
  loc: '#dc3545',
  rough: '#fd7e14',
  handle: 'var(--bs-primary, #0d6efd)',
};

const CSS = `
.jq-shopmap {
  --sm-paper: #f3f1ec; --sm-floor: #fdfcfa; --sm-grid: rgba(30,41,59,0.06); --sm-grid-major: rgba(30,41,59,0.12);
  --sm-ink: #1e293b; --sm-muted: #64748b; --sm-wall: #1e293b; --sm-door: #0f766e;
  --sm-rack-fill: rgba(234,88,12,0.10); --sm-beam: #ea580c; --sm-upright: #1d4ed8;
  --sm-machine: #e2e8f0; --sm-machine-line: #94a3b8; --sm-room: rgba(100,116,139,0.22);
  --sm-zone-line: rgba(100,116,139,0.55); --sm-pill-text: #ffffff; --sm-pill-bg: #ffffff;
}
[data-bs-theme="dark"] .jq-shopmap {
  --sm-paper: #15161d; --sm-floor: #1f212b; --sm-grid: rgba(226,232,240,0.05); --sm-grid-major: rgba(226,232,240,0.10);
  --sm-ink: #e2e8f0; --sm-muted: #94a3b8; --sm-wall: #cbd5e1; --sm-door: #2dd4bf;
  --sm-rack-fill: rgba(251,146,60,0.14); --sm-beam: #fb923c; --sm-upright: #60a5fa;
  --sm-machine: #2e3240; --sm-machine-line: #64748b; --sm-room: rgba(148,163,184,0.25);
  --sm-zone-line: rgba(148,163,184,0.5); --sm-pill-text: #ffffff; --sm-pill-bg: #1f212b;
}
.jq-shopmap .sm-pulse { animation: jq-sm-pulse 2.2s ease-out infinite; }
@keyframes jq-sm-pulse {
  0% { stroke-opacity: 0.55; stroke-width: 3px; }
  100% { stroke-opacity: 0; stroke-width: 16px; }
}
@media (prefers-reduced-motion: reduce) { .jq-shopmap .sm-pulse { animation: none; stroke-opacity: 0; } }
`;

const styles = {
  wrap: { position: 'relative', width: '100%' },
  svg: { display: 'block', width: '100%', height: 'auto', borderRadius: 10, border: '1px solid var(--bs-border-color, #dee2e6)' },
  text: { fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif', pointerEvents: 'none', userSelect: 'none' },
};

const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
const finite = (...vs) => vs.every((v) => Number.isFinite(num(v)));

/** An item's rectangle as [x0, y0, x1, y1] (normalised), or null. */
function itemRect(it) {
  const r = Array.isArray(it.rect) ? it.rect.map(Number) : null;
  if (!r || r.length !== 4 || !r.every(Number.isFinite)) return null;
  return [Math.min(r[0], r[2]), Math.min(r[1], r[3]), Math.max(r[0], r[2]), Math.max(r[1], r[3])];
}

/** A label's words in lines of at most `max` characters (a longer word keeps its own line). */
export function wrapWords(text, max) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const out = [];
  words.forEach((w) => {
    const last = out.length ? out[out.length - 1] : null;
    if (last !== null && `${last} ${w}`.length <= max) out[out.length - 1] = `${last} ${w}`;
    else out.push(w);
  });
  return out;
}

/**
 * A rack's frames and bays along its long side, from the item's `bays` ([{name, ft}], in order of
 * increasing x, or y for a rack running north-south): {frames: [pos], bays: [{name, a, b}]} in feet along
 * that axis. Frames are 3" uprights; when the bays and frames do not add up to the rack's length they are
 * scaled to it. Without bays: a frame at each end, one unnamed bay.
 */
export function rackBays(it, a0, a1) {
  const FRAME = 0.25;
  const list = (Array.isArray(it.bays) ? it.bays : [])
    .map((b) => ({ name: b && b.name ? String(b.name) : '', ft: Number(b && b.ft) }))
    .filter((b) => Number.isFinite(b.ft) && b.ft > 0);
  if (!list.length) return { frames: [a0, a1], bays: [{ name: '', a: a0, b: a1 }] };
  const want = list.reduce((s, b) => s + b.ft, 0) + FRAME * (list.length + 1);
  const k = (a1 - a0) / want;
  const frames = [a0 + (FRAME * k) / 2];
  const bays = [];
  let at = a0 + FRAME * k;
  list.forEach((b) => {
    bays.push({ name: b.name, a: at, b: at + b.ft * k });
    at += b.ft * k;
    frames.push(at + (FRAME * k) / 2);
    at += FRAME * k;
  });
  return { frames, bays };
}

/** A shop point under a client (screen) point: the SVG's own CTM inverted, then the y-flip. Null without a CTM. */
export function clientToShop(svg, clientX, clientY) {
  if (!svg || typeof svg.getScreenCTM !== 'function' || typeof svg.createSVGPoint !== 'function') return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const p = svg.createSVGPoint();
  p.x = clientX;
  p.y = clientY;
  const s = p.matrixTransform(ctm.inverse());
  return shopPt(s.x, s.y);
}

/** How wide the SVG is on screen (px): ResizeObserver where there is one; 640 under jsdom. */
function useRenderedWidth(ref) {
  const [w, setW] = useState(640);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const r = el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      if (r > 0) setW(r);
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

/** A scale bar length (ft) that draws 40-120 px wide at `ftPerPx`. */
function scaleFt(ftPerPx) {
  const steps = [2, 5, 10, 20, 25, 50, 100];
  return steps.find((s) => s / ftPerPx >= 48) || steps[steps.length - 1];
}

export default function ShopMap({
  map = null, geometry = null, anchors = null, gateways = null, zones = null,
  view = null, focus = null, coverage = null, location = null,
  markers = [], handles = [], onHandleMove = null, onMapClick = null, drawing = null,
  selectedZoneId = null, showAnchors = true, showGateways = true, showSpots = true, title = 'Shop map', maxHeight = null,
}) {
  const svgRef = useRef(null);
  const drag = useRef(null); // {key, moved, last} while a handle is held
  const suppressClick = useRef(false); // the click that ends a drag is not a map click
  const rawId = useId();
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const clipId = `shopmap-floor-${uid}`;
  const pxWide = useRenderedWidth(svgRef);

  const geo = geometry || (map && map.map ? map.map.geometry : null) || { items: [] };
  const items = Array.isArray(geo.items) ? geo.items : [];
  const anchorList = anchors || (map && map.anchors) || [];
  const gatewayList = gateways || (map && map.gateways) || [];
  const zoneList = (zones || (map && map.zones) || []).filter((z) => z && z.is_active !== false);
  const areas = zoneList.filter((z) => z.kind === 'area' && Array.isArray(z.polygon) && z.polygon.length >= 3);
  const spots = showSpots ? zoneList.filter((z) => z.kind === 'spot' && Array.isArray(z.polygon) && z.polygon.length >= 3) : [];
  const pzones = palletZones(zoneList);
  const hit = location ? locationZones(location, pzones) : { best: null, second: null };

  const bounds = mapBounds(geo);
  // a location with a zone answer zooms on its zones; a dock (or a map without zones) on the point
  const focusBox = () => (focus && hit.best ? zoneFocusBox(focus, pzones, bounds) : focusViewBox(focus, bounds));
  const box = view || (focus ? focusBox() : { x0: bounds[0], y0: bounds[1], x1: bounds[2], y1: bounds[3] });
  const vbW = box.x1 - box.x0;
  const vbH = box.y1 - box.y0;
  const k = vbW / Math.max(1, pxWide); // feet per screen pixel
  const px = (n) => n * k;
  const fontFt = px(11);

  const covGrid = useMemo(() => decodeHeat(coverage), [coverage]);
  const covUrl = useMemo(() => heatToDataUrl(covGrid, { palette: 'coverage' }), [covGrid]);

  const toShop = (e) => {
    const p = clientToShop(svgRef.current, e.clientX, e.clientY);
    return p ? { x: snapFt(p.x), y: snapFt(p.y) } : null;
  };

  const onPointerMove = (e) => {
    if (!drag.current || !onHandleMove) return;
    const p = toShop(e);
    if (!p) return;
    drag.current.moved = true;
    drag.current.last = p;
    onHandleMove(drag.current.key, p.x, p.y, { done: false });
  };
  const endDrag = () => {
    const d = drag.current;
    drag.current = null;
    if (d && d.moved && d.last && onHandleMove) onHandleMove(d.key, d.last.x, d.last.y, { done: true });
    suppressClick.current = !!(d && d.moved);
  };
  // A touch drag fires no click after it, so the flag set by endDrag would swallow the NEXT tap:
  // every new press starts clean (a handle's own press stops propagation, so it resets it too).
  // A press on the map itself never starts a drag: one still open lost its pointerup (the finger
  // left the map, capture failed), so it ends here instead of following this press around.
  const onPointerDown = () => {
    if (drag.current) endDrag();
    suppressClick.current = false;
  };
  const onClick = (e) => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (!onMapClick) return;
    const p = toShop(e);
    if (p) onMapClick(p.x, p.y);
  };

  const fits = (text, width, size = fontFt) => String(text || '').length * size * 0.58 <= width * 1.05;

  // ── layers ──
  const hitRgb = location && (location.rough || location.best === 'gateways') ? HIT.rough : HIT.loc;
  const zoneRole = (z) => (hit.best && z === hit.best ? 'best' : hit.second && z === hit.second ? 'second' : null);
  // the radio view (anchors on) is the editor's: there the floor areas and every zone keep a tint
  const tintAll = !hit.best;

  const areaFills = areas.map((z) => (
    <polygon key={`a${z.zone_id || z.name}`} points={svgPoints(z.polygon)} data-zone={z.name}
      fill={z.zone_id === selectedZoneId ? 'rgba(13,110,253,0.16)' : C.floor}
      stroke={z.zone_id === selectedZoneId ? C.handle : 'none'} strokeWidth={2} vectorEffect="non-scaling-stroke" />
  ));
  const zoneFills = pzones.map((z, i) => {
    const role = zoneRole(z);
    const tint = ZONE_TINTS[i % ZONE_TINTS.length];
    let fill = 'none';
    if (role === 'best') fill = `rgba(${hitRgb},0.2)`;
    else if (role === 'second') fill = `url(#sm-hatch-${uid})`;
    else if (tintAll) fill = `rgba(${tint},0.09)`;
    return (
      <polygon key={`p${z.zone_id || z.name}`} points={svgPoints(z.polygon)} data-pallet-zone={z.name}
        data-role={role || undefined} fill={z.zone_id === selectedZoneId ? 'rgba(13,110,253,0.22)' : fill}
        stroke={z.zone_id === selectedZoneId ? C.handle : (tintAll ? `rgba(${tint},0.6)` : C.zoneLine)}
        strokeWidth={1} strokeDasharray={role ? undefined : '5 4'} vectorEffect="non-scaling-stroke" />
    );
  });
  const spotLines = spots.map((z) => (
    <polygon key={`s${z.zone_id || z.name}`} points={svgPoints(z.polygon)} data-zone={z.name}
      fill={z.zone_id === selectedZoneId ? 'rgba(13,110,253,0.18)' : 'none'}
      stroke={z.zone_id === selectedZoneId ? C.handle : C.muted} strokeWidth={z.zone_id === selectedZoneId ? 2 : 1}
      strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
  ));
  const rooms = items.filter((it) => it.kind === 'room').map((it) => {
    const r = itemRect(it);
    if (r) return <rect key={it.id || it.name} x={r[0]} y={-r[3]} width={r[2] - r[0]} height={r[3] - r[1]} fill={`url(#sm-room-${uid})`} />;
    if (Array.isArray(it.pts)) return <polygon key={it.id || it.name} points={svgPoints(it.pts)} fill={`url(#sm-room-${uid})`} />;
    return null;
  });

  // walls: solid and about as thick as a real one once zoomed in (never under 3 px)
  const wallW = Math.max(0.6, px(3.2));
  const lines = [];
  items.filter((it) => (it.kind === 'wall' || it.kind === 'door') && Array.isArray(it.pts)).forEach((it, i) => {
    const key = it.id || `l${i}`;
    if (it.kind === 'wall') {
      const rf = !!it.rf && showAnchors; // the radio-lossy walls matter on the editor only
      lines.push(
        <polyline key={key} points={svgPoints(it.pts)} fill="none" data-kind="wall" data-rf={it.rf ? '1' : undefined}
          stroke={rf ? C.rfWall : C.wall} strokeWidth={wallW} strokeLinecap="square" strokeLinejoin="miter" />,
      );
      return;
    }
    // a door: the opening between two jambs, an overhead door dashed across it, a man door with its swing
    const [p0, p1] = it.pts.map((p) => [Number(p[0]), Number(p[1])]);
    const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
    if (!(len > 0)) return;
    const ux = (p1[0] - p0[0]) / len;
    const uy = (p1[1] - p0[1]) / len;
    const jamb = wallW * 0.9;
    const man = it.style === 'man' || (!it.style && /man door/i.test(String(it.name || '')));
    const into = Array.isArray(it.into) && finite(it.into[0], it.into[1]) ? [Number(it.into[0]), Number(it.into[1])] : null;
    const parts = [
      <polyline key="o" points={svgPoints(it.pts)} fill="none" data-kind="door" stroke={C.door}
        strokeWidth={man && into ? 1 : 1.5} strokeDasharray={man && into ? '2 3' : '5 3'} vectorEffect="non-scaling-stroke"
        strokeOpacity={man && into ? 0.5 : 1} />,
      ...[p0, p1].map((p, j) => (
        // eslint-disable-next-line react/no-array-index-key
        <line key={`j${j}`} x1={p[0] - uy * jamb} y1={-(p[1] + ux * jamb)} x2={p[0] + uy * jamb} y2={-(p[1] - ux * jamb)}
          stroke={C.wall} strokeWidth={Math.max(0.3, px(2))} />
      )),
    ];
    if (man && into) {
      const h = Number(it.hinge) === 1 ? p1 : p0;
      const o = h === p1 ? p0 : p1;
      const tip = [h[0] + into[0] * len, h[1] + into[1] * len];
      // screen coords (y down): sweep 1 = clockwise from the leaf's tip round to the far jamb
      const a = [tip[0] - h[0], -(tip[1] - h[1])];
      const b = [o[0] - h[0], -(o[1] - h[1])];
      const sweep = a[0] * b[1] - a[1] * b[0] > 0 ? 1 : 0;
      parts.push(
        <line key="leaf" x1={h[0]} y1={-h[1]} x2={tip[0]} y2={-tip[1]} stroke={C.door} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />,
        <path key="swing" d={`M ${tip[0]} ${-tip[1]} A ${len} ${len} 0 0 ${sweep} ${o[0]} ${-o[1]}`} fill="none" stroke={C.door}
          strokeWidth={1} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />,
      );
    }
    lines.push(
      <g key={key} data-door={it.name || key}>
        {it.name && <title>{it.name}</title>}
        {parts}
      </g>,
    );
  });

  const blocks = [];
  const labels = [];
  // the zones name the floor: the geometry's free labels go, and a rack named like a zone keeps no label
  const zoneNames = new Set(pzones.map((z) => String(z.name).trim().toLowerCase()));
  const smallFt = px(9);
  items.forEach((it, i) => {
    const key = it.id || `b${i}`;
    if (it.kind === 'rack' || it.kind === 'machine') {
      const r = itemRect(it);
      if (!r) return;
      const w = r[2] - r[0];
      const h = r[3] - r[1];
      const named = it.name && !zoneNames.has(String(it.name).trim().toLowerCase());
      if (it.kind === 'machine') {
        const rad = Math.min(w, h) * 0.12;
        blocks.push(
          <rect key={key} x={r[0]} y={-r[3]} width={w} height={h} rx={rad} data-kind="machine"
            fill={C.machine} stroke={C.machineLine} strokeWidth={1} vectorEffect="non-scaling-stroke">
            {it.name && <title>{it.name}</title>}
          </rect>,
        );
        if (named && fits(it.name, w) && h >= fontFt * 0.6) {
          labels.push(
            <text key={`t${key}`} x={(r[0] + r[2]) / 2} y={-(r[1] + r[3]) / 2} fontSize={fontFt} fill={C.ink}
              textAnchor="middle" dominantBaseline="central" style={{ ...styles.text, fontWeight: 600 }}>{it.name}</text>,
          );
        }
        return;
      }
      // a rack: its footprint, a beam down each long face, an upright at every frame, the bay names when they fit
      const alongX = w >= h;
      const { frames, bays } = alongX ? rackBays(it, r[0], r[2]) : rackBays(it, r[1], r[3]);
      const beam = Math.min((alongX ? h : w) * 0.18, Math.max(0.18, px(2)));
      const post = Math.max(0.25, px(2.4));
      const parts = [
        <rect key="f" x={r[0]} y={-r[3]} width={w} height={h} data-kind="rack" fill={C.rackFill}>
          {it.name && <title>{it.name}</title>}
        </rect>,
      ];
      if (alongX) {
        parts.push(<rect key="b0" x={r[0]} y={-r[3]} width={w} height={beam} fill={C.beam} />,
          <rect key="b1" x={r[0]} y={-r[1] - beam} width={w} height={beam} fill={C.beam} />);
        frames.forEach((f, j) => parts.push(
          // eslint-disable-next-line react/no-array-index-key
          <rect key={`u${j}`} x={f - post / 2} y={-r[3]} width={post} height={h} fill={C.upright} rx={post * 0.2} />,
        ));
      } else {
        parts.push(<rect key="b0" x={r[0]} y={-r[3]} width={beam} height={h} fill={C.beam} />,
          <rect key="b1" x={r[2] - beam} y={-r[3]} width={beam} height={h} fill={C.beam} />);
        frames.forEach((f, j) => parts.push(
          // eslint-disable-next-line react/no-array-index-key
          <rect key={`u${j}`} x={r[0]} y={-f - post / 2} width={w} height={post} fill={C.upright} rx={post * 0.2} />,
        ));
      }
      blocks.push(<g key={key} data-rack={it.name || key}>{parts}</g>);
      const depth = alongX ? h : w;
      const bayLabels = bays.filter((b) => b.name);
      const bayFits = bayLabels.length > 1 && depth >= smallFt * 0.95
        && bayLabels.every((b) => fits(b.name, b.b - b.a - post, smallFt) && (b.b - b.a) / k >= 26);
      if (bayFits) {
        bayLabels.forEach((b) => {
          const c = (b.a + b.b) / 2;
          labels.push(
            <text key={`t${key}-${b.name}`} x={alongX ? c : (r[0] + r[2]) / 2} y={alongX ? -(r[1] + r[3]) / 2 : -c}
              fontSize={smallFt} fill={C.ink} textAnchor="middle" dominantBaseline="central"
              style={{ ...styles.text, fontWeight: 700 }}>{b.name}</text>,
          );
        });
      } else if (named && fits(it.name, alongX ? w : h) && depth >= fontFt * 0.6) {
        const cx = (r[0] + r[2]) / 2;
        const cy = -(r[1] + r[3]) / 2;
        labels.push(
          <text key={`t${key}`} x={cx} y={cy} fontSize={fontFt} fill={C.ink} textAnchor="middle" dominantBaseline="central"
            transform={alongX ? undefined : `rotate(-90 ${cx} ${cy})`} style={{ ...styles.text, fontWeight: 600 }}>{it.name}</text>,
        );
      }
    } else if (it.kind === 'room' && it.name) {
      const r = itemRect(it);
      if (r && fits(it.name, r[2] - r[0], fontFt * 0.9)) {
        labels.push(
          <text key={`t${key}`} x={(r[0] + r[2]) / 2} y={-(r[1] + r[3]) / 2} fontSize={fontFt * 0.9} fill={C.muted}
            textAnchor="middle" dominantBaseline="central" style={styles.text}>{it.name}</text>,
        );
      }
    } else if (it.kind === 'label' && it.text && Array.isArray(it.at) && !pzones.length) {
      labels.push(
        <text key={`t${key}`} x={Number(it.at[0])} y={-Number(it.at[1])} fontSize={fontFt} fill={C.muted}
          textAnchor="middle" dominantBaseline="central" style={styles.text}>{it.text}</text>,
      );
    }
  });

  const colSize = Math.max(1.2, px(4));
  const columns = items.filter((it) => it.kind === 'column').map((it, i) => {
    let at = Array.isArray(it.at) ? it.at : null;
    const r = itemRect(it);
    if (!at && r) at = [(r[0] + r[2]) / 2, (r[1] + r[3]) / 2];
    if (!at && Array.isArray(it.pts) && it.pts[0]) [at] = it.pts;
    if (!at || !finite(at[0], at[1])) return null;
    return <rect key={it.id || `c${i}`} data-kind="column" x={Number(at[0]) - colSize / 2} y={-Number(at[1]) - colSize / 2}
      width={colSize} height={colSize} fill={C.wall} />;
  });

  const dia = px(7);
  const anchorMarks = showAnchors ? anchorList.filter((a) => finite(a.x_ft, a.y_ft)).map((a) => {
    const x = Number(a.x_ft);
    const y = -Number(a.y_ft);
    const grey = anchorIsGrey(a);
    return (
      <g key={`an${a.anchor_no}`} data-testid={`shopmap-anchor-${a.anchor_no}`} data-grey={grey ? '1' : '0'}>
        <title>{`Anchor ${a.anchor_no}${a.name ? ` (${a.name})` : ''}${grey ? (a.is_active === false ? ': switched off' : ': not placed') : ''}`}</title>
        <path d={`M ${x} ${y - dia} L ${x + dia} ${y} L ${x} ${y + dia} L ${x - dia} ${y} Z`}
          fill={grey ? C.anchorGrey : C.anchor} stroke="#fff" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <text x={x} y={y} fontSize={px(8)} fill="#fff" textAnchor="middle" dominantBaseline="central"
          style={{ ...styles.text, fontWeight: 700 }}>{a.anchor_no}</text>
      </g>
    );
  }) : null;

  const gwLabels = k <= 0.4;
  const gatewayMarks = showGateways ? gatewayList.filter((g) => finite(g.x_ft, g.y_ft)).map((g) => {
    const x = Number(g.x_ft);
    const y = -Number(g.y_ft);
    return (
      <g key={`gw${g.gateway_id}`} data-testid={`shopmap-gateway-${g.gateway_id}`}>
        <title>{`${g.gateway_id}${g.name ? ` (${g.name})` : ''}`}</title>
        <rect x={x - px(5)} y={y - px(5)} width={px(10)} height={px(10)} rx={px(2)}
          fill={g.is_active === false ? C.anchorGrey : C.gateway} stroke="#fff" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {gwLabels && (
          <text x={x + px(8)} y={y} fontSize={px(10)} fill={C.ink} dominantBaseline="central" style={styles.text}>{g.gateway_id}</text>
        )}
      </g>
    );
  }) : null;

  // zone names: the other zones' muted, wrapped to the zone (without its "(Rack C/D)" when that won't fit
  // on a line); the located ones on a pill with their strength, kept inside the view and clear of each other
  const pillOf = (z, role) => {
    const b = polyBox(z.polygon);
    if (!b) return null;
    const best = role === 'best';
    const size = fontFt * (best ? 1.1 : 0.95);
    const strength = zoneMapLine(best ? location.zone : location.zone2);
    const lines = [...wrapWords(z.name, 22), ...(strength ? [strength] : [])];
    const lh = size * 1.22;
    const padX = px(9);
    const padY = px(5);
    const textW = Math.max(...lines.map((ln, j) => ln.length * size * (strength && j === lines.length - 1 ? 0.5 : 0.6)));
    const w = textW + padX * 2;
    const h = lines.length * lh + padY * 2 - size * 0.22;
    const edge = px(6);
    const clampTo = (v, lo, hi) => (lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v)));
    const cx = clampTo((b[0] + b[2]) / 2, box.x0 + w / 2 + edge, box.x1 - w / 2 - edge);
    const top = clampTo(-(b[1] + b[3]) / 2 - h / 2, -box.y1 + edge, -box.y0 - h - edge);
    return { z, role, best, size, strength, lines, lh, padY, w, h, cx, top };
  };
  const pills = [];
  if (hit.best) pills.push(pillOf(hit.best, 'best'));
  if (hit.second) pills.push(pillOf(hit.second, 'second'));
  const [p1, p2] = pills;
  if (p1 && p2 && Math.abs(p1.cx - p2.cx) < (p1.w + p2.w) / 2 && Math.abs(p1.top - p2.top) < Math.max(p1.h, p2.h)) {
    // the second pill goes under the first (above it when there is no room below)
    const below = p1.top + p1.h + px(6);
    p2.top = below + p2.h <= -box.y0 - px(6) ? below : p1.top - p2.h - px(6);
  }
  const pillMarks = pills.filter(Boolean).map((q) => (
    <g key={`zl${q.z.zone_id || q.z.name}`} data-zone-pill={q.role}>
      <rect x={q.cx - q.w / 2} y={q.top} width={q.w} height={q.h} rx={Math.min(q.h / 2, px(10))}
        fill={q.best ? `rgb(${hitRgb})` : C.pillBg} stroke={`rgb(${hitRgb})`} strokeWidth={q.best ? 0 : 1.5}
        vectorEffect="non-scaling-stroke" filter={`url(#sm-shadow-${uid})`} />
      <text x={q.cx} y={q.top + q.padY + q.size * 0.5} fontSize={q.size} fill={q.best ? C.pillText : `rgb(${hitRgb})`}
        textAnchor="middle" dominantBaseline="central" data-zone-label={q.z.name} style={{ ...styles.text, fontWeight: 700 }}>
        {q.lines.map((ln, j) => {
          const meta = q.strength && j === q.lines.length - 1;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <tspan key={j} x={q.cx} dy={j === 0 ? 0 : q.lh} fontSize={meta ? q.size * 0.82 : undefined}
              fontWeight={meta ? 500 : undefined} opacity={meta ? 0.92 : undefined}>{ln}</tspan>
          );
        })}
      </text>
    </g>
  ));
  const zoneLabels = pzones.map((z, i) => {
    if (zoneRole(z)) return null;
    const b = polyBox(z.polygon);
    if (!b) return null;
    const w = b[2] - b[0];
    const cx = (b[0] + b[2]) / 2;
    const size = fontFt * 0.95;
    const perLine = Math.max(4, Math.floor(w / (size * 0.6)));
    const short = String(z.name).replace(/\s*\([^)]*\)\s*$/, '');
    const lines = wrapWords(String(z.name).length > perLine && short ? short : z.name, perLine);
    const lh = size * 1.15;
    const cy = -(b[1] + b[3]) / 2 - ((lines.length - 1) * lh) / 2;
    return (
      <text key={`zl${z.zone_id || z.name}`} x={cx} y={cy} fontSize={size} dominantBaseline="central" textAnchor="middle"
        fill={tintAll ? `rgba(${ZONE_TINTS[i % ZONE_TINTS.length]},0.95)` : C.muted} data-zone-label={z.name}
        style={{ ...styles.text, fontWeight: 600, opacity: tintAll ? 1 : 0.8 }}>
        <title>{z.name}</title>
        {lines.map((ln, j) => (
          // eslint-disable-next-line react/no-array-index-key
          <tspan key={j} x={cx} dy={j === 0 ? 0 : lh}>{ln}</tspan>
        ))}
      </text>
    );
  });
  const zoneOutlines = [hit.second, hit.best].filter(Boolean).flatMap((z) => {
    const best = z === hit.best;
    const out = [];
    if (best) {
      out.push(
        <polygon key={`zg${z.zone_id || z.name}`} points={svgPoints(z.polygon)} fill="none" stroke={`rgb(${hitRgb})`}
          strokeWidth={8} strokeOpacity={0.25} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />,
        <polygon key={`zp${z.zone_id || z.name}`} className="sm-pulse" points={svgPoints(z.polygon)} fill="none"
          stroke={`rgb(${hitRgb})`} strokeWidth={3} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />,
      );
    }
    out.push(
      <polygon key={`zo${z.zone_id || z.name}`} points={svgPoints(z.polygon)} fill="none" stroke={`rgb(${hitRgb})`}
        strokeWidth={best ? 2.5 : 2} strokeDasharray={best ? undefined : '6 4'} strokeLinejoin="round"
        vectorEffect="non-scaling-stroke" data-testid={best ? 'shopmap-zone-best' : 'shopmap-zone-second'} />,
    );
    return out;
  });
  let locMark = null;
  // a dot only when no zone is shaded: a dock, a map without pallet zones, or an estimate stored before
  // the zones (re-scored only when a gateway hears the beacon again: a quiet pallet keeps its old one)
  if (hasPoint(location) && !hit.best) {
    const x = Number(location.x_ft);
    const y = -Number(location.y_ft);
    const rough = !!location.rough || location.best === 'gateways';
    locMark = (
      <g data-testid="shopmap-location" data-rough={rough ? '1' : '0'}>
        <circle cx={x} cy={y} r={px(11)} fill={rough ? C.rough : C.loc} fillOpacity={0.18} />
        <circle cx={x} cy={y} r={px(5.5)} fill={rough ? C.rough : C.loc} stroke="#fff" strokeWidth={2}
          vectorEffect="non-scaling-stroke" />
      </g>
    );
  }

  const markerMarks = (markers || []).filter((m) => finite(m.x, m.y)).map((m) => (
    <g key={m.key} data-testid={`shopmap-marker-${m.key}`} onClick={m.onClick ? (e) => { e.stopPropagation(); m.onClick(m); } : undefined}
      style={{ cursor: m.onClick ? 'pointer' : 'default' }}>
      {m.title && <title>{m.title}</title>}
      {m.shape === 'cross' ? (
        <path d={`M ${m.x - px(5)} ${-m.y - px(5)} L ${m.x + px(5)} ${-m.y + px(5)} M ${m.x - px(5)} ${-m.y + px(5)} L ${m.x + px(5)} ${-m.y - px(5)}`}
          stroke={m.color || '#0d6efd'} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
      ) : (
        <circle cx={m.x} cy={-m.y} r={px(4.5)} fill={m.color || '#0d6efd'} stroke="#fff" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      )}
      {m.label && (
        <text x={m.x + px(6)} y={-m.y} fontSize={px(10)} fill={C.ink} dominantBaseline="central" style={styles.text}>{m.label}</text>
      )}
    </g>
  ));

  const handleMarks = (handles || []).filter((h) => finite(h.x, h.y)).map((h) => (
    <circle key={`h${h.key}`} cx={Number(h.x)} cy={-Number(h.y)} r={px(10)} fill="rgba(13,110,253,0.12)" stroke={C.handle}
      strokeWidth={1.5} vectorEffect="non-scaling-stroke" role="button" tabIndex={0} aria-label={`Move ${h.label || h.key}`}
      data-testid={`shopmap-handle-${h.key}`} style={{ cursor: 'grab', touchAction: 'none' }}
      onPointerDown={(e) => {
        e.stopPropagation();
        suppressClick.current = false;
        if (e.currentTarget.setPointerCapture && e.pointerId !== undefined) {
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* jsdom */ }
        }
        drag.current = { key: h.key, moved: false };
      }}
      onKeyDown={(e) => {
        if (!onHandleMove) return;
        const step = e.shiftKey ? 5 : 0.5;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
        if (!d) return;
        e.preventDefault();
        // only the axis that moved snaps: a nudge east never shifts the point north
        const x = d[0] ? snapFt(Number(h.x) + d[0]) : Number(h.x);
        const y = d[1] ? snapFt(Number(h.y) + d[1]) : Number(h.y);
        onHandleMove(h.key, x, y, { done: true });
      }} />
  ));

  const drawingMark = Array.isArray(drawing) && drawing.length > 0 ? (
    <g data-testid="shopmap-drawing">
      <polyline points={svgPoints(drawing.length >= 3 ? [...drawing, drawing[0]] : drawing)} fill="rgba(13,110,253,0.1)"
        stroke={C.handle} strokeWidth={2} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
      {drawing.map(([x, y], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <circle key={i} cx={x} cy={-y} r={px(3.5)} fill={C.handle} />
      ))}
    </g>
  ) : null;

  // the north arrow (north is +x, to the right) and the scale bar on one chip, in the first corner (top-left,
  // top-right, bottom-left, bottom-right) where it covers no rack, machine, wall or zone pill
  const sFt = scaleFt(k);
  const nr = px(9);
  const chipH = px(28);
  const chipW = px(40) + sFt + px(10) + `${sFt} ft`.length * px(9.5) * 0.6 + px(8);
  const inset = px(8);
  const obstacles = [];
  items.forEach((it) => {
    const r = (it.kind === 'rack' || it.kind === 'machine') ? itemRect(it) : null;
    if (r) obstacles.push([r[0], -r[3], r[2], -r[1]]);
    if ((it.kind === 'wall' || it.kind === 'door') && Array.isArray(it.pts)) {
      for (let j = 1; j < it.pts.length; j += 1) {
        const [ax, ay] = it.pts[j - 1].map(Number);
        const [bx, by] = it.pts[j].map(Number);
        obstacles.push([Math.min(ax, bx) - wallW, -Math.max(ay, by) - wallW, Math.max(ax, bx) + wallW, -Math.min(ay, by) + wallW]);
      }
    }
  });
  pills.filter(Boolean).forEach((q) => obstacles.push([q.cx - q.w / 2, q.top, q.cx + q.w / 2, q.top + q.h]));
  const corners = [
    [box.x0 + inset, -box.y1 + inset], [box.x1 - inset - chipW, -box.y1 + inset],
    [box.x0 + inset, -box.y0 - inset - chipH], [box.x1 - inset - chipW, -box.y0 - inset - chipH],
  ];
  const clear = ([cx0, cy0]) => !obstacles.some((o) => o[0] < cx0 + chipW && cx0 < o[2] && o[1] < cy0 + chipH && cy0 < o[3]);
  const [chipX, chipY] = corners.find(clear) || corners[0];
  const ncx = chipX + px(16);
  const ncy = chipY + chipH / 2;
  const sx = chipX + px(40);
  const sy = ncy + px(4);
  const tick = px(4);
  const legend = (
    <g data-testid="shopmap-legend">
      <rect x={chipX} y={chipY} width={chipW} height={chipH} rx={px(7)} fill={C.paper} fillOpacity={0.88}
        stroke={C.zoneLine} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <g data-testid="shopmap-north">
        <title>North is to the right</title>
        <circle cx={ncx} cy={ncy} r={nr} fill="none" stroke={C.muted} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <path d={`M ${ncx + nr * 0.8} ${ncy} L ${ncx - nr * 0.45} ${ncy - nr * 0.5} L ${ncx - nr * 0.15} ${ncy} L ${ncx - nr * 0.45} ${ncy + nr * 0.5} Z`}
          fill={C.ink} />
        <text x={ncx + nr + px(4)} y={ncy} fontSize={px(9)} fill={C.ink} dominantBaseline="central"
          style={{ ...styles.text, fontWeight: 700 }}>N</text>
      </g>
      <g data-testid="shopmap-scale">
        <path d={`M ${sx} ${sy - tick} V ${sy} H ${sx + sFt} V ${sy - tick} M ${sx + sFt / 2} ${sy} V ${sy - tick * 0.6}`}
          fill="none" stroke={C.ink} strokeWidth={1.25} vectorEffect="non-scaling-stroke" />
        <text x={sx + sFt + px(6)} y={sy - tick / 2} fontSize={px(9.5)} fill={C.ink} dominantBaseline="central"
          style={styles.text}>{`${sFt} ft`}</text>
      </g>
    </g>
  );
  const rectOf = (g) => heatRect(g);
  const editing = !!(onMapClick || (handles && handles.length));
  const minor = 5;
  const major = 20;

  return (
    <div style={styles.wrap}>
      <svg
        ref={svgRef}
        className="jq-shopmap"
        viewBox={viewBoxOf(box)}
        role="img"
        aria-label={title}
        data-testid="shop-map"
        data-view={viewBoxOf(box)}
        preserveAspectRatio="xMidYMid meet"
        style={{ ...styles.svg, aspectRatio: `${vbW} / ${vbH}`, background: C.paper, maxHeight: maxHeight || undefined,
          cursor: onMapClick ? 'crosshair' : 'default', touchAction: editing ? 'none' : 'auto' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onClick}
      >
        <title>{title}</title>
        <style>{CSS}</style>
        <defs>
          <clipPath id={clipId}>
            {areas.map((z) => <polygon key={`c${z.zone_id || z.name}`} points={svgPoints(z.polygon)} />)}
          </clipPath>
          <pattern id={`sm-grid-${uid}`} width={minor} height={minor} patternUnits="userSpaceOnUse">
            <path d={`M ${minor} 0 H 0 V ${minor}`} fill="none" stroke={C.grid} strokeWidth={px(1)} />
          </pattern>
          <pattern id={`sm-grid20-${uid}`} width={major} height={major} patternUnits="userSpaceOnUse">
            <rect width={major} height={major} fill={`url(#sm-grid-${uid})`} />
            <path d={`M ${major} 0 H 0 V ${major}`} fill="none" stroke={C.gridMajor} strokeWidth={px(1)} />
          </pattern>
          <pattern id={`sm-hatch-${uid}`} width={px(7)} height={px(7)} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={px(7)} height={px(7)} fill={`rgba(${hitRgb},0.07)`} />
            <line x1={0} y1={0} x2={0} y2={px(7)} stroke={`rgba(${hitRgb},0.4)`} strokeWidth={px(1.6)} />
          </pattern>
          <pattern id={`sm-room-${uid}`} width={px(6)} height={px(6)} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1={0} y1={0} x2={0} y2={px(6)} stroke={C.room} strokeWidth={px(1.2)} />
          </pattern>
          <filter id={`sm-shadow-${uid}`} x="-20%" y="-20%" width="140%" height="160%">
            <feDropShadow dx={0} dy={px(1.5)} stdDeviation={px(2)} floodColor="#0f172a" floodOpacity={0.25} />
          </filter>
          <filter id={`sm-lift-${uid}`} x="-5%" y="-5%" width="110%" height="115%">
            <feDropShadow dx={0} dy={px(2)} stdDeviation={px(4)} floodColor="#0f172a" floodOpacity={0.12} />
          </filter>
        </defs>
        <g data-layer="1-areas">
          <g filter={`url(#sm-lift-${uid})`}>{areaFills}</g>
          {areas.length > 0 && (
            <rect x={box.x0} y={-box.y1} width={vbW} height={vbH} fill={`url(#sm-grid20-${uid})`} clipPath={`url(#${clipId})`}
              pointerEvents="none" data-testid="shopmap-grid" />
          )}
          {zoneFills}{rooms}{spotLines}
        </g>
        <g data-layer="2-coverage" clipPath={areas.length ? `url(#${clipId})` : undefined}>
          {covGrid && covUrl && <image href={covUrl} {...rectOf(covGrid)} preserveAspectRatio="none" data-testid="shopmap-coverage" />}
        </g>
        <g data-layer="3-walls">{lines}</g>
        <g data-layer="4-blocks">{blocks}{labels}</g>
        <g data-layer="5-columns">{columns}</g>
        <g data-layer="6-anchors">{anchorMarks}</g>
        <g data-layer="7-gateways">{gatewayMarks}</g>
        <g data-layer="8-location">{zoneOutlines}{zoneLabels}{pillMarks}{locMark}</g>
        <g data-layer="9-markers">{markerMarks}</g>
        <g data-layer="10-edit">{drawingMark}{handleMarks}</g>
        <g data-layer="11-legend">{legend}</g>
      </svg>
    </div>
  );
}
